"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createInboxSaveApptCreateResponse = exports.createInboxPayload = exports.createSaveCustomAvailabilityPayload = exports.createInboxSaveApptCreatePayload = exports.createInboxSaveLinkCreatePayload = exports.createToolsResponse = exports.createResourceResponse = exports.createRoomResponse = exports.createSearchResponse = exports.createAvailabilityResponse = exports.createCalendarCreatePayload = exports.getServiceOptions = exports.getLocationOptions = exports.getTemplateOptions = exports.getOptions = exports.getUsers = exports.getUserType = void 0;
const constants_1 = require("@/app/shared/constants");
const helpers_1 = require("@/app/shared/helpers");
const scheduler_1 = require("@/app/SUMOCEP/store/scheduler");
const moment_timezone_1 = __importDefault(require("moment-timezone"));
const generated_api_1 = require("./generated-api");
const types_1 = require("./types");
const convertUserTypes = {
    [types_1.SalesforceUserType.USER]: "USER" /* MemberSubType.USER */,
    [types_1.SalesforceUserType.CONTACT]: "CONTACT" /* MemberSubType.CONTACT */,
    [types_1.SalesforceUserType.LEAD]: "LEAD" /* MemberSubType.LEAD */,
    [types_1.SalesforceUserType.RESOURCE]: "RESOURCE" /* MemberSubType.RESOURCE */,
};
const getUserType = (type) => {
    return convertUserTypes[type] || type;
};
exports.getUserType = getUserType;
const getUsers = (users) => {
    return users.map((user) => {
        const userData = user.data;
        return {
            id: userData.Id,
            name: userData.Name,
            type: "SALESFORCE" /* MemberType.SALESFORCE */,
            subType: (0, exports.getUserType)(userData.attributes.type),
            email: userData.Email,
            phoneNumber: userData.MobilePhone,
        };
    });
};
exports.getUsers = getUsers;
const getOptions = (options, prefix, fallback) => {
    return options.map(({ type = '', value = '', label = '' }) => ({
        type: type.replace('__c', '').replace(prefix + '__', '') || fallback,
        value,
        label,
    }));
};
exports.getOptions = getOptions;
const getTemplateOptions = (options, prefix) => {
    return options.map((option) => {
        const data = option.data;
        const locationOptions = data[prefix + 'LocationOptions__c']?.split(';') ?? [];
        const availabilityOptions = data[prefix + 'AvailabilityOptions__c']?.split(';') ?? [];
        const services = data[prefix + 'Services__c']?.split(';') ?? [];
        const inviteTemplateType = data[prefix + 'InviteTemplateType__c'];
        const dynamicTravelTimeStrategy = data[prefix + 'DynamicTravelTimeStrategy__c'];
        const slotIntervalGranularity = data[prefix + 'SlotIntervalGranularity__c'];
        const multipleServiceSelection = data[prefix + 'MultipleServiceSelection__c'];
        const defaultTimeZone = data[prefix + 'DefaultTimezone__c'];
        const availableTimezones = data[prefix + 'AvailableTimezones__c'];
        const availableTimezonesList = availableTimezones?.split(';') ?? [];
        const maxServicesPerAppointment = data[prefix + 'MaxServicesPerAppointment__c'];
        return {
            id: option.value,
            name: option.label,
            isDefault: option.isDefault,
            locationOptions,
            availabilityOptions,
            services,
            inviteTemplateType,
            dynamicTravelTimeStrategy,
            multipleServiceSelection,
            slotIntervalGranularity,
            defaultTimeZone,
            availableTimezonesList,
            maxServicesPerAppointment
        };
    });
};
exports.getTemplateOptions = getTemplateOptions;
const getLocationOptions = (options) => {
    return options.map((option) => {
        const { value = '', label = '' } = option;
        return {
            type: (0, scheduler_1.convertFromLocationTypeToSchedulerLocationTab)(value),
            value,
            label,
        };
    });
};
exports.getLocationOptions = getLocationOptions;
const getServiceOptions = (options, prefix) => {
    return options.map((option) => {
        const { type = '', value = '', label = '' } = option;
        const data = option.data;
        const bufferTimePre = data[prefix + 'BufferTimePre__c'] || 0;
        const bufferTimePost = data[prefix + 'BufferTimePost__c'] || 0;
        const beforeStartTime = bufferTimePre + 'm';
        const afterEndTime = bufferTimePost + 'm';
        const hasBuffer = bufferTimePre > 0 || bufferTimePost > 0;
        const locationOptions = data[prefix + 'LocationOptions__c']?.split(';') ?? [];
        return {
            type: type.replace('__c', ''),
            value,
            label,
            beforeStartTime,
            afterEndTime,
            hasBuffer,
            locationOptions,
        };
    });
};
exports.getServiceOptions = getServiceOptions;
const createCalendarCreatePayload = (params, template, serviceDurations) => {
    const { bookingPage } = params;
    if (!bookingPage)
        throw new Error('Booking page is required');
    if (!template)
        throw new Error('Template is required');
    const { whoId, slotsAttendeeIds, timezoneId, serviceIds, locationId, locationIds, resourcesIds, duration } = (0, exports.createInboxPayload)(bookingPage);
    const startMoment = (0, moment_timezone_1.default)(params.startAt).tz(timezoneId);
    const endMoment = (0, moment_timezone_1.default)(params.finishAt).tz(timezoneId);
    const startDate = startMoment.format('YYYY-MM-DD');
    const endDate = endMoment.format('YYYY-MM-DD');
    const startTime = startMoment.format('HH:mm');
    const endTime = endMoment.format('HH:mm');
    return {
        type: generated_api_1.InviteSaveRequestTypeEnum.DateSpecific,
        organizer: whoId,
        attendees: slotsAttendeeIds.length ? [slotsAttendeeIds] : [],
        resources: resourcesIds.length ? [resourcesIds] : [],
        locationIds: locationId ? [locationId] : locationIds,
        timezoneId,
        timeframe: {
            startDate,
            startTime,
            endDate,
            endTime,
            timezoneId,
        },
        services: serviceIds.map((id) => ({ id, duration: serviceDurations?.[id] ?? duration })),
        customAvailability: {
            timezoneId,
            alwaysAvailable: false,
        },
        duration: null,
        travelTimeBefore: 0,
        travelTimeAfter: 0,
        locationLat: null,
        locationLon: null,
        dynamicTravelTimeStrategy: template.dynamicTravelTimeStrategy,
    };
};
exports.createCalendarCreatePayload = createCalendarCreatePayload;
const createAvailabilityResponse = (data, params, hostMeetings) => {
    const available = data?.weekAvailability?.available ?? [];
    const events = data?.events ?? [];
    const timeZone = params.bookingPage?.calendar?.timeZone ?? scheduler_1.DEFAULT_BOOKING_PAGE.calendar.timeZone;
    const availability = available.flatMap((dayAvailability, dayIndex) => {
        const currentDay = (0, moment_timezone_1.default)(params.startAt).tz(timeZone).startOf('day').add(dayIndex, 'days');
        return dayAvailability.map(({ startTime, endTime }) => ({
            startsAt: currentDay.clone().add(startTime, 'minutes').valueOf(),
            finishesAt: currentDay.clone().add(endTime, 'minutes').valueOf(),
        }));
    });
    console.log(`🚀 ~ createAvailabilityResponse ~ events:`, events);
    const meetings = events
        .filter((event) => {
        return event.id !== undefined && event.subject !== undefined && event.startTime !== undefined && event.endTime !== undefined;
    })
        .map(({ id, subject, startTime, endTime }) => ({
        userId: id,
        title: subject,
        startsAt: new Date(startTime).getTime(),
        finishesAt: new Date(endTime).getTime(),
    }));
    return {
        availability,
        meetings: [...hostMeetings, ...meetings],
        serviceLimitReached: data?.weekAvailability?.serviceLimitReached ?? false,
    };
};
exports.createAvailabilityResponse = createAvailabilityResponse;
const createSearchResponse = (data, body) => {
    return {
        templateId: body.templateId,
        locationId: body.locationId,
    };
};
exports.createSearchResponse = createSearchResponse;
const createRoomResponse = (rooms = [], prefix) => {
    return rooms.map((room) => {
        const data = room.data;
        const name = data[prefix + 'Name'];
        const location = data[prefix + 'Location__c'];
        return {
            id: data.Id,
            type: room.type?.replace('__c', ''),
            name,
            location,
        };
    });
};
exports.createRoomResponse = createRoomResponse;
const createResourceResponse = (resources = [], prefix) => {
    return resources.map((resource) => {
        const data = resource.data;
        const name = data[prefix + 'Name'];
        const location = data[prefix + 'Location__c'];
        return {
            id: data.Id,
            type: resource.type?.replace('__c', ''),
            name,
            location,
        };
    });
};
exports.createResourceResponse = createResourceResponse;
const createToolsResponse = (tools = [], prefix) => {
    return tools.map((tool) => {
        const name = tool[prefix + 'AppointmentType__c'];
        const url = tool[prefix + 'InviteLink__c'];
        return {
            id: tool.Id,
            name,
            url,
        };
    });
};
exports.createToolsResponse = createToolsResponse;
const createInboxSaveLinkCreatePayload = (params) => {
    const { bookingPage, serviceDurations } = params;
    if (!bookingPage)
        throw new Error('Booking page is required');
    const { additionalAttendees, saveAttendeeIds, clientId, colorCode, duration, endDateTime, expirationDate, rollingWindow, includeWebConfDetails, limitNumber, location, name, note, postBufferTime, preBufferTime, processId, relatedId, resourcesIds, startDatetime, timezoneId, serviceIds, whatIds, whereId, whoId, placeId, } = (0, exports.createInboxPayload)(bookingPage);
    const locationType = (0, scheduler_1.convertToInviteSaveRequestLocationTypeEnum)(bookingPage.where?.defaultLocationType);
    const services = serviceIds.map((id) => ({ id, duration: serviceDurations?.[id] ?? null }));
    return {
        additionalAttendees,
        anyTimeAvailability: bookingPage.when?.availability === "CUSTOM" /* SchedulerAvailability.CUSTOM */,
        attendeeIds: saveAttendeeIds.flat(),
        availabilityOption: (0, scheduler_1.convertToAvailabilityOption)(bookingPage.when?.availability),
        clientId,
        colorCode,
        duration,
        endDateTime,
        expirationDate,
        includeWebConfDetails,
        isRecurring: false,
        limitNumber,
        limitType: generated_api_1.InviteSaveRequestLimitTypeEnum.PARTICIPANT,
        location,
        locationType,
        name,
        note,
        postBufferTime,
        preBufferTime,
        processId,
        relatedId,
        resourcesIds,
        rollingWindow,
        startDatetime,
        timezoneId,
        type: generated_api_1.InviteSaveRequestTypeEnum.DateSpecific,
        videoConferenceDetailsType: generated_api_1.InviteSaveRequestVideoConferenceDetailsTypeEnum.Host,
        whatIds,
        whereId,
        whoId,
        placeId,
        services,
    };
};
exports.createInboxSaveLinkCreatePayload = createInboxSaveLinkCreatePayload;
const createInboxSaveApptCreatePayload = (params) => {
    const { bookingPage, serviceDurations } = params;
    if (!bookingPage)
        throw new Error('Booking page is required');
    const payload = (0, exports.createInboxPayload)(bookingPage);
    const { additionalAttendees, appointmentTime, saveAttendeeIds, clientId, colorCode, duration, location, name, note, noteForAdditionalAttendees, postBufferTime, preBufferTime, processId, relatedId, resourcesIds, serviceIds, timezoneId, whatIds, whereId, whoId, placeId, } = payload;
    if (!clientId)
        throw new Error('Client ID is required');
    const locationType = (0, scheduler_1.convertToAppointmentSaveRequestLocationTypeEnum)(bookingPage.where?.defaultLocationType);
    const services = serviceIds.map((id) => ({ id, duration: serviceDurations?.[id] ?? null }));
    return {
        additionalAttendees,
        appointmentTime,
        attendeeIds: saveAttendeeIds,
        clientId,
        colorCode,
        duration,
        location,
        locationType,
        name,
        note,
        noteForAdditionalAttendees,
        postBufferTime,
        preBufferTime,
        processId,
        relatedId,
        reminder: generated_api_1.AppointmentSaveRequestReminderEnum.DO_NOT_SEND,
        resourceIds: resourcesIds.length ? [resourcesIds] : [],
        timezoneId,
        waitlist: generated_api_1.AppointmentSaveRequestWaitlistEnum.DO_NOT_SEND,
        whatIds,
        whereId,
        whoId,
        placeId,
        services,
    };
};
exports.createInboxSaveApptCreatePayload = createInboxSaveApptCreatePayload;
const createSaveCustomAvailabilityPayload = (params, data) => {
    const { bookingPage } = params;
    if (!bookingPage)
        throw new Error('Booking page is required');
    if (!data.inviteId)
        throw new Error('Invite ID is required');
    const payload = (0, exports.createInboxPayload)(bookingPage);
    const { timezoneId, customAvailability, locationId } = payload;
    return {
        type: generated_api_1.InviteAvailabilitySaveRequestTypeEnum.DateSpecific,
        inviteId: data.inviteId,
        timezoneId,
        locationId,
        anyTimeAvailability: false,
        isOverlapped: false,
        customAvailability,
    };
};
exports.createSaveCustomAvailabilityPayload = createSaveCustomAvailabilityPayload;
const createInboxPayload = (bookingPage) => {
    const invitee = bookingPage.who?.mainGuest;
    const guests = bookingPage.who?.guests || [];
    const attendees = [invitee, ...guests];
    const hosts = bookingPage.who?.hostMembers || [];
    const SalesforceHosts = hosts.filter((host) => {
        return Boolean(host?.type !== "CUSTOM" /* MemberType.CUSTOM */ && host?.id);
    });
    const SlotsSalesforceAttendees = attendees.filter((guest) => {
        return Boolean(guest?.id && guest?.type !== "CUSTOM" /* MemberType.CUSTOM */ &&
            (guest?.subType === "USER" /* MemberSubType.USER */ || guest?.subType === "RESOURCE" /* MemberSubType.RESOURCE */));
    });
    const SaveSalesforceAttendees = attendees.filter((guest) => {
        return Boolean(guest?.type !== "CUSTOM" /* MemberType.CUSTOM */ && guest?.id);
    });
    const OtherAttendees = [...attendees, ...hosts].filter((guest) => {
        return Boolean(guest?.type === "CUSTOM" /* MemberType.CUSTOM */ && guest?.email);
    });
    const timeZone = bookingPage.calendar?.timeZone ?? scheduler_1.DEFAULT_BOOKING_PAGE.calendar.timeZone;
    const startDate = bookingPage.when?.dateRange?.from ? new Date(bookingPage.when.dateRange.from) : undefined;
    const endDate = bookingPage.when?.dateRange?.to ? new Date(bookingPage.when.dateRange.to) : undefined;
    const startDatetime = startDate?.toISOString();
    const endDateTime = endDate?.toISOString();
    const expirationDate = bookingPage.when?.expiresAt ? new Date(bookingPage.when.expiresAt).toLocaleDateString('en-CA') : undefined;
    const postBufferTime = helpers_1.bufferTimeConvert.fromStringToNumber(bookingPage.when?.afterEndTime ?? scheduler_1.DEFAULT_BOOKING_PAGE.when.afterEndTime);
    const preBufferTime = helpers_1.bufferTimeConvert.fromStringToNumber(bookingPage.when?.beforeStartTime ?? scheduler_1.DEFAULT_BOOKING_PAGE.when.beforeStartTime);
    const whereId = bookingPage.where?.locations?.[0] ?? scheduler_1.DEFAULT_BOOKING_PAGE.where.locations?.[0];
    const serviceIds = bookingPage.what?.serviceIds ?? [];
    const customRanges = bookingPage.when?.customRanges || [];
    const customAvailability = customRanges.map((range) => {
        const datex = new Date(range.start).toLocaleDateString('en-CA');
        const startDate = new Date(range.start);
        const endDate = new Date(range.end);
        const startTime = startDate.getHours() * 60 + startDate.getMinutes() - preBufferTime;
        const endTime = endDate.getHours() * 60 + endDate.getMinutes() + postBufferTime;
        const day = new Date(range.start).getDay();
        return {
            datex,
            day,
            timezoneId: timeZone,
            ranges: [
                {
                    selected: true,
                    startTime,
                    endTime,
                    serviceId: serviceIds[0],
                    locationId: whereId,
                    originalLocationId: whereId,
                },
            ],
        };
    });
    const mainGuestValidSubTypes = ["CONTACT" /* MemberSubType.CONTACT */, "LEAD" /* MemberSubType.LEAD */];
    const isValidClient = Boolean(invitee?.type !== "CUSTOM" /* MemberType.CUSTOM */ && invitee?.subType && mainGuestValidSubTypes.includes(invitee.subType));
    const clientId = isValidClient && invitee?.id ? invitee.id : undefined;
    const location = bookingPage?.where?.locationItems?.find((location) => location.value === whereId);
    const locationIds = bookingPage?.where?.locationItems?.map((location) => location.value)?.filter(Boolean) ?? [];
    const { type: placeType, label: locationName, value: locationId } = location || {};
    const customCountryCode = bookingPage.where?.customCountryPhoneCode ?? scheduler_1.DEFAULT_BOOKING_PAGE.where.customCountryPhoneCode;
    const customPhone = bookingPage.where?.customPhone ?? undefined;
    const locationType = bookingPage.where?.locationTypes?.[0];
    const rollingWindow = bookingPage.when?.rollingWindowDays ?? undefined;
    const duration = bookingPage.when?.duration?.count ?? scheduler_1.DEFAULT_BOOKING_PAGE.when.duration.count;
    const timeUnit = bookingPage.when?.duration?.timeUnit ?? scheduler_1.DEFAULT_BOOKING_PAGE.when.duration.timeUnit;
    const durationInMinutes = timeUnit === constants_1.TimeUnit.MINUTE ? duration : duration * 60;
    const isInPerson = locationType === "IN_PERSON" /* SchedulerLocationTab.IN_PERSON */;
    const isVideoConference = locationType === "VIDEO_CONFERENCE" /* SchedulerLocationTab.VIDEO_CONFERENCE */;
    const resourcesIds = isInPerson ? (bookingPage.where?.locationRooms ?? []) : [];
    const getLocation = () => {
        switch (locationType) {
            case "VIDEO_CONFERENCE" /* SchedulerLocationTab.VIDEO_CONFERENCE */:
                return undefined;
            case "PHONE_CALL" /* SchedulerLocationTab.PHONE_CALL */:
                return `${customCountryCode}${customPhone}`;
            case "IN_PERSON" /* SchedulerLocationTab.IN_PERSON */:
                return locationName;
            default:
                return undefined;
        }
    };
    return {
        additionalAttendees: OtherAttendees.map((attendee) => attendee.email),
        anyTimeAvailability: false,
        appointmentTime: customRanges[0]?.start ? new Date(customRanges[0].start).toISOString() : undefined,
        slotsAttendeeIds: SlotsSalesforceAttendees.map((attendee) => attendee.id),
        saveAttendeeIds: SaveSalesforceAttendees.map((attendee) => [attendee.id]),
        availabilityOption: (0, scheduler_1.convertToAvailabilityOption)(bookingPage.when?.availability),
        clientId,
        colorCode: bookingPage.what?.color ?? scheduler_1.DEFAULT_BOOKING_PAGE.what.color,
        customAvailability,
        duration: durationInMinutes,
        endDateTime,
        expirationDate,
        includeWebConfDetails: isVideoConference,
        isRecurring: false,
        limitNumber: bookingPage.guestsLimit ?? scheduler_1.DEFAULT_BOOKING_PAGE.guestsLimit,
        location: getLocation(),
        name: bookingPage.what?.customName ?? scheduler_1.DEFAULT_BOOKING_PAGE.what.customName,
        note: bookingPage.what?.noteFromHost ?? scheduler_1.DEFAULT_BOOKING_PAGE.what.noteFromHost,
        noteForAdditionalAttendees: '',
        postBufferTime,
        preBufferTime,
        processId: bookingPage.CEPTemplate?.id,
        relatedId: clientId,
        resourcesIds,
        rollingWindow,
        startDatetime,
        timezoneId: timeZone,
        serviceIds,
        whatIds: serviceIds,
        whoId: SalesforceHosts.map((host) => host?.id),
        whereId: placeType !== constants_1.LocationPlaceType.GOOGLE_LOCATION && isInPerson ? locationId : undefined,
        placeId: placeType === constants_1.LocationPlaceType.GOOGLE_LOCATION && isInPerson ? locationId : undefined,
        placeType,
        locationId: isInPerson ? locationId : undefined,
        locationIds,
    };
};
exports.createInboxPayload = createInboxPayload;
const createInboxSaveApptCreateResponse = (response) => {
    return {};
};
exports.createInboxSaveApptCreateResponse = createInboxSaveApptCreateResponse;
